import { Currency } from '@kodiak-finance/sdk-core'
import CurrencyLogo from 'components/CurrencyLogo'
import { useConnection } from 'hooks/useConnection'
import useTheme from 'hooks/useTheme'
import { useTokenChart } from 'hooks/useTokenChart'
import { useId } from 'react'
import { createPortal } from 'react-dom'
import { Area, AreaChart, ReferenceDot, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import SmallAmount from './SmallAmount'

const CARD_WIDTH = 300
const CARD_PADDING = 16
const CHART_WIDTH = CARD_WIDTH - 2 * CARD_PADDING
const CHART_HEIGHT = 110
// Tallest the card gets (chart, stats and source note), for keeping it on screen.
const CARD_MAX_HEIGHT = 340
const GAP = 12

const Card = styled.div`
  position: fixed;
  z-index: 1000;
  width: ${CARD_WIDTH}px;
  padding: ${CARD_PADDING}px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  background: ${({ theme }) => theme.cardPrimary};
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
  pointer-events: none;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 15px;
`

const Price = styled.div`
  margin-top: 8px;
  font-size: 26px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`

const Change = styled.div<{ $up: boolean }>`
  margin-top: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};

  span {
    color: ${({ theme, $up }) => ($up ? theme.success500 : theme.error500)};
  }
`

const ChartArea = styled.div`
  margin-top: 12px;
  background-image: radial-gradient(${({ theme }) => theme.cardBorderSecondary} 1px, transparent 1px);
  background-size: 20px 20px;
`

const Stats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};

  b {
    display: block;
    margin-top: 2px;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.textPrimary};
  }
`

const SourceNote = styled.div`
  margin-top: 10px;
  font-size: 11px;
  color: ${({ theme }) => theme.textSecondary};
  opacity: 0.6;
`

const SOURCE_LABEL = { coingecko: 'CoinGecko', dexscreener: 'DexScreener' } as const

/**
 * Beside the dialog, level with the hovered row: on its right, else its left;
 * undefined when neither side has room (narrow screens).
 */
export function cardPosition(
  anchor: DOMRect,
  container: DOMRect | undefined,
  viewport: { width: number; height: number }
) {
  const box = container ?? anchor
  const right = box.right + GAP
  const left = right + CARD_WIDTH <= viewport.width ? right : box.left - GAP - CARD_WIDTH
  if (left < 0) return undefined
  const top = Math.min(Math.max(GAP, anchor.top - 40), viewport.height - CARD_MAX_HEIGHT - GAP)
  return { left, top }
}

function Usd({ value }: { value: number }) {
  return value < 1 ? <SmallAmount value={value} prefix="$" /> : <>{formatDollarAmount(value, 2, false)}</>
}

export default function TokenHoverCard({
  currency,
  price,
  anchor,
  container,
}: {
  currency: Currency
  price: number
  anchor: DOMRect
  container?: DOMRect
}) {
  const theme = useTheme()
  const gradientId = useId()
  const { chainId } = useConnection()
  const { data } = useTokenChart(chainId, currency.wrapped.address)

  const position = cardPosition(anchor, container, { width: window.innerWidth, height: window.innerHeight })
  if (!position || !data) return null

  const points = data.points
  const change = data.priceChange24h
  const isUp = (change ?? 0) >= 0
  const color = isUp ? theme.success500 : theme.error500
  // Dollar move implied by the same percent the label shows, so the two never disagree.
  const changeUsd = change !== null ? Math.abs(price - price / (1 + change / 100)) : null
  const series = points.map(([time, value]) => ({ time, price: value }))

  return createPortal(
    <Card style={position}>
      <Header>
        <CurrencyLogo currency={currency} size="24px" />
        {currency.symbol}
      </Header>
      <Price>
        <Usd value={price} />
      </Price>
      {change !== null && changeUsd !== null && (
        <Change $up={isUp}>
          <span>{isUp ? '▲' : '▼'}</span> <Usd value={changeUsd} /> ({Math.abs(change).toFixed(2)}%) today
        </Change>
      )}
      {series.length > 1 && (
        <ChartArea>
          <AreaChart
            width={CHART_WIDTH}
            height={CHART_HEIGHT}
            data={series}
            margin={{ top: 6, right: 6, bottom: 6, left: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis hide dataKey="time" type="number" domain={['dataMin', 'dataMax']} />
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Area
              type="monotone"
              dataKey="price"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
            />
            <ReferenceDot
              x={series[series.length - 1].time}
              y={series[series.length - 1].price}
              r={4}
              fill={color}
              stroke="none"
              ifOverflow="extendDomain"
            />
          </AreaChart>
        </ChartArea>
      )}
      <Stats>
        <div>
          24h volume<b>{data.volume24h ? formatDollarAmount(data.volume24h) : '–'}</b>
        </div>
        <div>
          Liquidity<b>{data.liquidity ? formatDollarAmount(data.liquidity) : '–'}</b>
        </div>
      </Stats>
      {data.statsSource && <SourceNote>Data from {SOURCE_LABEL[data.statsSource]}</SourceNote>}
    </Card>,
    document.body
  )
}
